import React from 'react';
import ModernCTA from '../../components/ModernCTA';

const EnterpriseSoftwarePage = () => {
  return (
    <div className="enterprise-page" style={{ marginTop: '110px' }}>
      {/* Animated Background Elements */}
      <div className="animated-bg-element bg-1"></div>
      <div className="animated-bg-element bg-2"></div>
      <div className="animated-bg-element bg-3"></div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="gradient-text">Transform Your Business</span> with Intelligent Enterprise Solutions
          </h1>
          <p className="hero-subtitle">
            Custom-built ERP/CRM systems infused with machine learning and predictive analytics to drive operational excellence and customer intelligence
          </p>
          <div className="hero-features">
            <span className="feature-bubble">
              <span className="bubble-icon">🤖</span> AI Automation
            </span>
            <span className="feature-bubble">
              <span className="bubble-icon">📊</span> Real-time Analytics
            </span>
            <span className="feature-bubble">
              <span className="bubble-icon">🔮</span> Predictive Insights
            </span>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="solutions-section">
        <div className="section-header">
          <h2 className="section-title">Tailored <span className="gradient-text">Enterprise Solutions</span></h2>
          <p className="section-description">
            We architect systems that grow with your business, integrating seamlessly with your existing infrastructure while providing intelligent automation.
          </p>
        </div>
        
        <div className="solution-cards">
          <div className="solution-card">
            <div className="card-icon">📦</div>
            <h3 className="card-title">Custom ERP Systems</h3>
            <p className="card-desc">
              End-to-end enterprise resource planning solutions with modules for finance, inventory, HR, manufacturing, and supply chain management.
            </p>
            <ul className="card-features">
              <li><span className="feature-highlight">Machine Learning:</span> Automated invoice processing</li>
              <li><span className="feature-highlight">Predictive Analytics:</span> Inventory demand forecasting</li>
              <li><span className="feature-highlight">Integration:</span> Legacy system modernization</li>
              <li><span className="feature-highlight">Security:</span> Role-based access controls</li>
            </ul>
          </div>
          
          <div className="solution-card">
            <div className="card-icon">🤝</div>
            <h3 className="card-title">AI-Powered CRM</h3>
            <p className="card-desc">
              Next-generation customer relationship management with embedded intelligence for sales automation and customer experience enhancement.
            </p>
            <ul className="card-features">
              <li><span className="feature-highlight">Sentiment Analysis:</span> Email and chat monitoring</li>
              <li><span className="feature-highlight">Lead Scoring:</span> Predictive opportunity ranking</li>
              <li><span className="feature-highlight">Automation:</span> Smart follow-up scheduling</li>
              <li><span className="feature-highlight">Integration:</span> Marketing stack connectivity</li>
            </ul>
          </div>
          
          <div className="solution-card">
            <div className="card-icon">📈</div>
            <h3 className="card-title">Business Intelligence</h3>
            <p className="card-desc">
              Advanced analytics platform with customizable dashboards, KPI tracking, and predictive modeling capabilities.
            </p>
            <ul className="card-features">
              <li><span className="feature-highlight">Forecasting:</span> Revenue and demand prediction</li>
              <li><span className="feature-highlight">Visualization:</span> Interactive data exploration</li>
              <li><span className="feature-highlight">Alerts:</span> Anomaly detection notifications</li>
              <li><span className="feature-highlight">APIs:</span> Data export and integration</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="tech-section">
        <div className="section-header">
          <h2 className="section-title">Our <span className="gradient-text">Technology Stack</span></h2>
          <p className="section-description">
            We leverage cutting-edge technologies to build robust, scalable enterprise solutions
          </p>
        </div>
        
        <div className="tech-grid">
          <div className="tech-item">
            <div className="tech-icon">⎔</div>
            <h3 className="tech-title">Backend</h3>
            <p className="tech-desc">
              Node.js, Python, Java, .NET Core, PostgreSQL, MongoDB, Redis, Kafka
            </p>
          </div>
          
          <div className="tech-item">
            <div className="tech-icon">Δ</div>
            <h3 className="tech-title">AI/ML</h3>
            <p className="tech-desc">
              TensorFlow, PyTorch, Scikit-learn, NLP, Computer Vision, Forecasting
            </p>
          </div>
          
          <div className="tech-item">
            <div className="tech-icon">◯</div>
            <h3 className="tech-title">Frontend</h3>
            <p className="tech-desc">
              React, Angular, Vue.js, TypeScript, Web Components, Micro-frontends
            </p>
          </div>
          
          <div className="tech-item">
            <div className="tech-icon">☁</div>
            <h3 className="tech-title">Cloud</h3>
            <p className="tech-desc">
              AWS, Azure, GCP, Kubernetes, Docker, Serverless, CI/CD Pipelines
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="case-section">
        <div className="section-header">
          <h2 className="section-title">Success <span className="gradient-text">Stories</span></h2>
          <p className="section-description">
            How we've transformed businesses across industries
          </p>
        </div>
        
        <div className="case-studies">
          <div className="case-study">
            <h3 className="case-title">Manufacturing ERP</h3>
            <p className="case-desc">
              Implemented predictive maintenance system reducing downtime by 40% and inventory costs by 28% for automotive parts manufacturer.
            </p>
            <div className="case-stats">
              <div className="stat">
                <div className="stat-value">40%</div>
                <div className="stat-label">Downtime Reduction</div>
              </div>
              <div className="stat">
                <div className="stat-value">28%</div>
                <div className="stat-label">Cost Savings</div>
              </div>
            </div>
          </div>
          
          <div className="case-study">
            <h3 className="case-title">Healthcare CRM</h3>
            <p className="case-desc">
              AI-driven patient engagement platform improving appointment adherence by 35% and patient satisfaction scores by 22 points.
            </p>
            <div className="case-stats">
              <div className="stat">
                <div className="stat-value">35%</div>
                <div className="stat-label">Adherence Increase</div>
              </div>
              <div className="stat">
                <div className="stat-value">22 pts</div>
                <div className="stat-label">Satisfaction Gain</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <ModernCTA />
      </section>

      {/* CSS Styles */}
      <style jsx>{`
        .enterprise-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #e2e8f0;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          min-height: 100vh;
          position: relative;
          overflow-x: hidden;
        }
        
        /* Animated Background Elements */
        .animated-bg-element {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0.15;
          animation: float 15s infinite ease-in-out;
        }
        
        .bg-1 {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
          top: 10%;
          left: -100px;
          animation-delay: 0s;
        }
        
        .bg-2 {
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #c084fc 0%, #f472b6 100%);
          bottom: 10%;
          right: -150px;
          animation-delay: 3s;
        }
        
        .bg-3 {
          width: 250px;
          height: 250px;
          background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
          top: 50%;
          left: 30%;
          animation-delay: 6s;
        }
        
        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(20px, 20px); }
          50% { transform: translate(-20px, 10px); }
          75% { transform: translate(10px, -20px); }
        }
        
        /* Hero Section */
        .hero-section {
          padding: 6rem 2rem 8rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        
        .hero-content {
          max-width: 900px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }
        
        .hero-title {
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
          animation: fadeInUp 1s ease;
        }
        
        .hero-subtitle {
          font-size: 1.25rem;
          line-height: 1.6;
          max-width: 700px;
          margin: 0 auto 2.5rem;
          opacity: 0.9;
          animation: fadeInUp 1s ease 0.2s forwards;
          opacity: 0;
        }
        
        .hero-features {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
          animation: fadeInUp 1s ease 0.4s forwards;
          opacity: 0;
        }
        
        .feature-bubble {
          background: rgba(100, 116, 139, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(226, 232, 240, 0.1);
          padding: 0.75rem 1.5rem;
          border-radius: 2rem;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .feature-bubble:hover {
          background: rgba(100, 116, 139, 0.4);
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 5px 15px -5px rgba(56, 189, 248, 0.3);
        }
        
        .bubble-icon {
          font-size: 1.1rem;
        }
        
        /* Gradient Text */
        .gradient-text {
          background: linear-gradient(90deg, #38bdf8 0%, #818cf8 50%, #c084fc 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: gradientShift 8s ease infinite;
          background-size: 200% 200%;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Sections Common Styles */
        section {
          padding: 5rem 2rem;
          position: relative;
          z-index: 2;
        }
        
        .section-header {
          max-width: 800px;
          margin: 0 auto 4rem;
          text-align: center;
        }
        
        .section-title {
          font-size: 2.5rem;
          margin-bottom: 1.5rem;
          font-weight: 700;
          letter-spacing: -0.025em;
        }
        
        .section-description {
          color: #94a3b8;
          font-size: 1.1rem;
          line-height: 1.6;
        }
        
        /* Solutions Section */
        .solution-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .solution-card {
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(148, 163, 184, 0.1);
          border-radius: 1.25rem;
          padding: 2.5rem 2rem;
          transition: all 0.4s ease;
          backdrop-filter: blur(10px);
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        
        .solution-card:hover {
          transform: translateY(-10px) rotateX(5deg);
          border-color: rgba(148, 163, 184, 0.3);
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.3);
          background: rgba(15, 23, 42, 0.7);
        }
        
        .card-icon {
          font-size: 2.5rem;
          margin-bottom: 1.5rem;
          display: inline-block;
          transition: transform 0.3s ease;
        }
        
        .solution-card:hover .card-icon {
          transform: scale(1.2);
        }
        
        .card-title {
          font-size: 1.5rem;
          margin-bottom: 1.25rem;
          font-weight: 600;
          position: relative;
          display: inline-block;
        }
        
        .card-title:after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #38bdf8 0%, #818cf8 100%);
          transition: width 0.3s ease;
        }
        
        .solution-card:hover .card-title:after {
          width: 100%;
        }
        
        .card-desc {
          color: #94a3b8;
          margin-bottom: 1.5rem;
          line-height: 1.6;
          min-height: 4.5rem;
        }
        
        .card-features {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .card-features li {
          position: relative;
          padding-left: 1.75rem;
          margin-bottom: 0.85rem;
          color: #cbd5e1;
          transition: all 0.3s ease;
        }
        
        .card-features li:before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.6em;
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 50%;
          background: #38bdf8;
          transform: scale(0);
          transition: transform 0.3s ease;
        }
        
        .solution-card:hover .card-features li:before {
          transform: scale(1);
        }
        
        .feature-highlight {
          font-weight: 600;
          color: #38bdf8;
        }
        
        /* Technology Section */
        .tech-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .tech-item {
          background: rgba(15, 23, 42, 0.4);
          border-radius: 1rem;
          padding: 2rem 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          border: 1px solid rgba(148, 163, 184, 0.05);
        }
        
        .tech-item:hover {
          background: rgba(15, 23, 42, 0.7);
          transform: translateY(-5px);
          border-color: rgba(56, 189, 248, 0.2);
          box-shadow: 0 10px 20px -10px rgba(56, 189, 248, 0.1);
        }
        
        .tech-icon {
          font-size: 2rem;
          margin-bottom: 1.5rem;
          display: inline-block;
          transition: transform 0.3s ease;
        }
        
        .tech-item:hover .tech-icon {
          transform: rotate(15deg) scale(1.2);
        }
        
        .tech-title {
          font-size: 1.25rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        
        .tech-desc {
          color: #94a3b8;
          font-size: 0.95rem;
          line-height: 1.6;
        }
        
        /* Case Studies */
        .case-studies {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .case-study {
          background: rgba(15, 23, 42, 0.5);
          border-radius: 1rem;
          padding: 2rem;
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }
        
        .case-study:hover {
          border-left-color: #38bdf8;
          transform: translateX(5px);
          background: rgba(15, 23, 42, 0.7);
        }
        
        .case-title {
          font-size: 1.5rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        
        .case-desc {
          color: #94a3b8;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }
        
        .case-stats {
          display: flex;
          gap: 1.5rem;
        }
        
        .stat {
          text-align: center;
        }
        
        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          background: linear-gradient(90deg, #38bdf8 0%, #818cf8 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        
        .stat-label {
          font-size: 0.85rem;
          color: #94a3b8;
        }
        
        /* CTA Section */
        .cta-section {
          padding: 6rem 2rem;
          text-align: center;
        }
        
        /* Responsive Adjustments */
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2.5rem;
          }
          
          .hero-subtitle {
            font-size: 1.1rem;
          }
          
          .section-title {
            font-size: 2rem;
          }
          
          section {
            padding: 4rem 1.5rem;
          }
          
          .solution-card, .tech-item, .case-study {
            padding: 1.75rem;
          }
        }
        
        @media (max-width: 480px) {
          .hero-title {
            font-size: 2rem;
          }
          
          .hero-section {
            padding: 4rem 1rem 6rem;
          }
          
          .section-title {
            font-size: 1.75rem;
          }
          
          .case-stats {
            flex-direction: column;
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default EnterpriseSoftwarePage;